import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { venderTef, type MetodoTef, type ResultadoTefNormalizado } from '../lib/tef/elgin';
import { useGetLojaAtualQuery, useRegistrarPagamentoTefMutation } from '../api';
import { getLocalImpressaoTotem } from '../lib/totem-config';
import { chaveDaTransacao, documentoComprovante, imprimeComprovanteTef, imprimeNoAparelho, imprimirComInsistencia } from '../lib/impressao-totem';
import { formatarBRL } from '../lib/formato';
import { iniciarOperacaoCritica } from '../lib/operacao-critica';

const ROTULO_METODO: Record<MetodoTef, string> = {
  CREDIT_CARD: 'Cartão de crédito',
  DEBIT_CARD: 'Cartão de débito',
  PIX: 'PIX',
};

function IconeMetodo({ metodo }: { metodo: MetodoTef }): JSX.Element {
  if (metodo === 'PIX') return <QrCode2Icon />;
  if (metodo === 'DEBIT_CARD') return <PaymentsIcon />;
  return <CreditCardIcon />;
}

type Fase = 'metodo' | 'processando' | 'erro' | 'registro-pendente';

/**
 * Fluxo de pagamento TEF ELGIN no TOTEM. Dispara a venda no app local (pinpad),
 * registra o resultado no backend e sinaliza sucesso/erro. Bloqueante durante o
 * processamento. Se houver mais de um método, mostra a escolha; com um único
 * método, inicia direto (sem tela extra). Ver docs/spec-pagamento-online.md §5.7.
 */
export function DialogPagamentoTef({
  aberto,
  pedidoId,
  valorCentavos,
  metodos,
  onSucesso,
  onCancelar,
}: {
  aberto: boolean;
  pedidoId: string;
  valorCentavos: number;
  metodos: MetodoTef[];
  onSucesso: () => void;
  onCancelar: () => void;
}): JSX.Element {
  const [fase, setFase] = useState<Fase>('metodo');
  const [mensagem, setMensagem] = useState<string | null>(null);
  const aprovadoPendente = useRef<{ metodo: MetodoTef; resultado: ResultadoTefNormalizado } | null>(null);
  const [registrar] = useRegistrarPagamentoTefMutation();
  const { data: loja } = useGetLojaAtualQuery();
  const metodoUnico = metodos.length === 1 ? metodos[0] ?? null : null;
  // Evita disparar a venda automática mais de uma vez.
  const autoIniciado = useRef(false);
  // Cartão cobrado e ainda fora do pedido: o vigia de inatividade não pode reiniciar o totem nesse meio.
  const encerrarCritica = useRef<(() => void) | null>(null);

  function segurarVigia(): void {
    if (!encerrarCritica.current) encerrarCritica.current = iniciarOperacaoCritica();
  }

  function soltarVigia(): void {
    encerrarCritica.current?.();
    encerrarCritica.current = null;
  }

  useEffect(() => soltarVigia, []);

  async function processar(metodo: MetodoTef): Promise<void> {
    setFase('processando');
    setMensagem(null);

    let resultado: ResultadoTefNormalizado;
    try {
      resultado = await venderTef(metodo, valorCentavos);
    } catch (err) {
      setMensagem(extrairMsg(err) ?? 'Não foi possível processar o pagamento no terminal. Tente novamente.');
      setFase('erro');
      return;
    }

    if (!resultado.aprovado) {
      setMensagem(resultado.mensagem ?? 'Pagamento não aprovado. Tente novamente ou use outra forma.');
      setFase('erro');
      return;
    }

    aprovadoPendente.current = { metodo, resultado };
    segurarVigia();
    await registrarNoPedido(metodo, resultado);
  }

  async function registrarNoPedido(metodo: MetodoTef, resultado: ResultadoTefNormalizado): Promise<void> {
    setFase('processando');
    try {
      const noAparelho = imprimeNoAparelho();
      const resp = await registrar({
        idPedido: pedidoId,
        metodo,
        aprovado: resultado.aprovado,
        uniqueId: resultado.uniqueId,
        nsu: resultado.nsu,
        bandeira: resultado.bandeira,
        authorizationCode: resultado.authorizationCode,
        adquirente: resultado.adquirente,
        dataTransacao: resultado.dataTransacao,
        // Comprovante (via cliente) + local de impressão configurado neste totem
        // — o backend dispara a impressão no servidor do Sischef.
        comprovante: resultado.comprovante,
        localImpressao: noAparelho ? undefined : getLocalImpressaoTotem() || undefined,
        raw: resultado.raw,
      }).unwrap();

      if (resp.status === 'CONFIRMED') {
        const comprovante = noAparelho && imprimeComprovanteTef(loja?.configuracao) ? documentoComprovante(resultado.comprovante) : null;
        if (comprovante) {
          void imprimirComInsistencia(
            `${pedidoId}:TEF:${chaveDaTransacao(resultado.uniqueId, resultado.nsu, resultado.dataTransacao)}`,
            async () => [comprovante],
            loja?.impressaoLocal?.servidor ?? null,
          );
        }

        aprovadoPendente.current = null;
        soltarVigia();
        onSucesso();
        return;
      }

      setMensagem('O pagamento foi aprovado, mas não conseguimos registrá-lo no pedido.');
      setFase('registro-pendente');
    } catch (err) {
      setMensagem(
        extrairMsg(err) ??
          'O pagamento foi aprovado, mas não conseguimos registrá-lo no pedido. Chame um atendente.',
      );
      setFase('registro-pendente');
    }
  }

  // Método único: inicia a venda automaticamente ao abrir (uma vez).
  useEffect(() => {
    if (aberto && metodoUnico && !autoIniciado.current) {
      autoIniciado.current = true;
      void processar(metodoUnico);
    }
    if (!aberto) {
      autoIniciado.current = false;
      aprovadoPendente.current = null;
      soltarVigia();
      setFase('metodo');
      setMensagem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, metodoUnico]);

  function tentarNovamente(): void {
    const pendente = aprovadoPendente.current;

    if (pendente) {
      void registrarNoPedido(pendente.metodo, pendente.resultado);
      return;
    }

    if (metodoUnico) void processar(metodoUnico);
    else setFase('metodo');
  }

  const bloqueado = fase === 'processando' || fase === 'registro-pendente';

  return (
    <Dialog
      open={aberto}
      onClose={bloqueado ? undefined : onCancelar}
      fullWidth
      maxWidth="xs"
      disableEscapeKeyDown={bloqueado}
    >
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography variant="h6">Pagamento no terminal</Typography>
          <Typography variant="h4" fontWeight={700}>
            {formatarBRL(valorCentavos / 100)}
          </Typography>

          {fase === 'metodo' && !metodoUnico && (
            <>
              <Typography color="text.secondary">Escolha como pagar:</Typography>
              <Stack spacing={1.5} sx={{ width: '100%' }}>
                {metodos.map((m) => (
                  <Button
                    key={m}
                    variant="outlined"
                    size="large"
                    startIcon={<IconeMetodo metodo={m} />}
                    onClick={() => void processar(m)}
                  >
                    {ROTULO_METODO[m]}
                  </Button>
                ))}
              </Stack>
              <Button color="inherit" onClick={onCancelar}>
                Cancelar
              </Button>
            </>
          )}

          {fase === 'processando' && (
            <>
              <CircularProgress />
              <Typography color="text.secondary">
                {aprovadoPendente.current
                  ? 'Pagamento aprovado. Registrando no pedido…'
                  : 'Siga as instruções na maquininha (aproxime, insira ou passe o cartão).'}
              </Typography>
            </>
          )}

          {fase === 'registro-pendente' && (
            <>
              <Box sx={{ color: 'warning.main', display: 'flex' }}>
                <ErrorOutlineIcon fontSize="large" />
              </Box>
              <Typography fontWeight={600}>Pagamento aprovado</Typography>
              <Typography color="text.secondary">{mensagem}</Typography>
              <Stack spacing={1} sx={{ width: '100%' }}>
                <Button variant="contained" size="large" onClick={tentarNovamente}>
                  Tentar registrar de novo
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Se não resolver, chame um atendente e mostre esta tela.
                </Typography>
              </Stack>
            </>
          )}

          {fase === 'erro' && (
            <>
              <Box sx={{ color: 'error.main', display: 'flex' }}>
                <ErrorOutlineIcon fontSize="large" />
              </Box>
              <Typography color="error">{mensagem}</Typography>
              <Stack spacing={1} sx={{ width: '100%' }}>
                <Button variant="contained" size="large" onClick={tentarNovamente}>
                  Tentar novamente
                </Button>
                <Button color="inherit" onClick={onCancelar}>
                  Cancelar
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function extrairMsg(err: unknown): string | null {
  if (typeof err === 'object' && err && 'data' in err) {
    const d = (err as { data?: { mensagem?: string } }).data;
    return d?.mensagem ?? null;
  }
  return null;
}
